(ns manage.ad-variant.details-ocp.preview
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Slider$default" :as Slider]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["react-use/lib/useMeasure$default" :as useMeasure]
            ["src/svg/controls/media_player_call/media-pause.svg$default"
             :as MediaPause]
            ["src/svg/controls/media_player_call/media-play.svg$default"
             :as MediaPlay]
            ["src/svg/controls/media_player_call/volume-slash.svg$default"
             :as VolumeSlash]
            ["src/svg/controls/media_player_call/volume-wave-2.svg$default"
             :as Volume]
            [common.widgets.preview-container :refer [preview-container]]
            [common.widgets.video-timeline :refer [seconds-to-str]]
            [manage.ad-variant-preview.data :as variant-preview-data]
            [manage.ad-variant-preview.hooks :as variant-preview-hooks]
            [manage.ad-variant-preview.meta :refer [preview-format-selector]]
            [manage.ad-variant-preview.viewport :as viewport]
            [manage.ad-variant-preview.widgets.preview-item :refer [preview-content]]
            [manage.ad-variant.data :as variant-data]
            [manage.ad-variant.widgets :as w]
            [uix.core :as uix :refer [$ defui]]))

(defn- round-to-nearest-100 [n]
  (* 100 (Math/round (/ n 100))))

(defui video-player-controls [{:keys [variant video-status video-controls]}]
  (let [{:keys [uuid]} variant
        {:keys [playing
                toggle-play
                timeline-disabled
                timeline-progress
                set-timeline-progress
                restore-timeline-progress
                audio-active-variant
                toggle-audio-for-variant
                max-duration]}
        (variant-preview-hooks/use-video-controls {:video-status video-status
                                                   :video-controls video-controls})

        audio-active (= uuid audio-active-variant)]

    ($ Stack
       {:sx #js {:p 1
                 :borderBottom 1
                 :borderColor "grey.60"}}
       ($ Stack
          {:direction "row"
           :justifyContent "space-between"
           :alignItems "center"
           :gap 1}
          ($ Stack
             {:direction "row"
              :alignItems "center"
              :gap 0.5}
             ($ IconButton
                {:onClick toggle-play
                 :size "small"}
                ($ SvgIcon
                   (if playing ($ MediaPause) ($ MediaPlay))))
             ($ Typography
                {:variant "caption"
                 :noWrap true}
                (str (seconds-to-str timeline-progress)
                     " / "
                     (seconds-to-str max-duration))))
          ($ IconButton
             {:onClick (fn []
                         (toggle-audio-for-variant uuid))
              :size "small"}
             ($ SvgIcon
                (if audio-active ($ Volume) ($ VolumeSlash)))))
       ($ Slider
          {:value timeline-progress
           :max max-duration
           :disabled timeline-disabled

           ;; NOTE: most video is probably around 24-30fps, max 120-240fps for some special cases?
           ;; Currently 30fps
           :step (/ 1 30)
           :onChange (fn [_e v]
                       (set-timeline-progress v {:save-position true}))
           :onChangeCommitted restore-timeline-progress

           :sx #js {"& .MuiSlider-track" #js {:transition "none"
                                              :borderWidth 0}
                    "& .MuiSlider-thumb" #js {:transition "none"
                                              :height "12px"
                                              :width "12px"
                                              :visibility "hidden"
                                              "&:hover, &.Mui-active, &.Mui-focusVisible" #js {:boxShadow "none"}}
                    "&:hover .MuiSlider-thumb" #js {:visibility "visible"}}}))))

(defui variant-preview
  [{:keys [variant hide-variant-info fill-available-height disable-fullscreen carousel-controls]
    :or {hide-variant-info false}}]
  (let [{:keys [components ad-type dimensions]} variant
        main-component (variant-data/get-main-component components ad-type)
        [width height] (variant-preview-data/get-card-dimensions
                        {:ad-type ad-type
                         :component main-component
                         :template-dimensions dimensions})
        meta? (variant-data/meta? ad-type)
        video? (= :video (variant-data/get-component-type ad-type))
        carousel? (= :carousel (variant-data/get-component-type ad-type))
        ;; Unlike the preview page, there's no page-level control here that already
        ;; shows the format selector, so show it inline above the preview whenever
        ;; there's actually something to switch between.
        show-preview-format? (and carousel?
                                  (variant-preview-data/carousel-multi-placement? (:value main-component)))
        ;; Formats with nothing uploaded for them yet, so the selector can grey
        ;; them out instead of letting them be picked just to show "no asset".
        disabled-formats (when show-preview-format?
                           (variant-preview-data/unavailable-preview-formats (:value main-component)))
        [preview-format set-preview-format] (uix/use-state (first variant-preview-data/preview-formats))
        [current-card-has-video? set-current-card-has-video!] (uix/use-state false)
        show-video-controls? (or video? (and carousel? current-card-has-video?))

        event-target (uix/use-ref (js/EventTarget.))
        [video-status set-video-status] (uix/use-state {})
        [video-controls set-video-controls] (uix/use-state {})
        [measure-ref measure] (useMeasure)
        [format-bar-ref format-bar-measure] (useMeasure)
        ctx (uix/use-memo (fn []
                            {:event-target @event-target
                             :set-video-controls set-video-controls
                             :preview-format preview-format
                             :set-preview-format set-preview-format})
                          [preview-format])
        measured-height (.-height measure)
        format-bar-height (.-height format-bar-measure)]
    ($ Box {:ref measure-ref
            :sx #js {:flex 1 :minHeight 0}}
       ($ viewport/CtxProvider
          {:value ctx}
          (when show-preview-format?
            ($ Box {:ref format-bar-ref
                    :sx #js {:pb 2
                             :mb 2
                             :borderBottom "1px solid"
                             :borderColor "divider"}}
               ($ preview-format-selector {:format preview-format
                                           :set-format set-preview-format
                                           :disabled-formats disabled-formats})))
          ($ preview-container
             {:width (cond-> width
                       ;; Since a border is added for meta previews,
                       ;; we need to offset the width with the border width.
                       meta? (+ 2))
              :height (cond-> height
                        ;; The meta height doesn't take into account the extra height
                        ;; that the meta-wrapper adds. We need to offset the height with
                        ;; this extra height of the meta-wrapper to make sure the whole
                        ;; preview is in view.
                        meta? (+ 240)
                        (and meta? carousel?) (- 180))

              ;; Fill available height: parent height - control-height - format-bar height
              ;; Only use measured height if available, otherwise fallback
              :container-height (if fill-available-height
                                  (when (pos? measured-height)
                                    (- measured-height 56 (if show-video-controls? 77 0) format-bar-height))
                                  (round-to-nearest-100 (/ js/window.innerHeight 2)))
              :max-zoom 5
              :scroll-zoom-enabled false
              :toolbar-buttons (when-not hide-variant-info
                                 ($ w/ad-variant-type-info
                                    {:ad-variant variant}))
              :disable-fullscreen disable-fullscreen
              :preview ($ Box {:sx (when meta?
                                     #js {:border "1px solid"
                                          :borderColor "grey.60"})}
                          ($ preview-content
                             {:variant variant
                              :set-video-status set-video-status
                              :video-controls video-controls
                              :on-current-card-video-change set-current-card-has-video!
                              :carousel-controls carousel-controls}))
              :preview-controls (when show-video-controls?
                                  ($ video-player-controls
                                     {:variant variant
                                      :video-status video-status
                                      :video-controls video-controls}))})))))
